/* eslint-disable no-loop-func */
import { BaseAnnotationTool } from "./base/BaseAnnotationTool.js";

// State
import { getToolState } from "component/toolManager/toolState.js";
import toolStyle from "component/toolManager/toolStyle.js";
import toolColors from "component/toolManager/toolColors.js";
import textStyle from "component/toolManager/textStyle.js";

// Drawing
import {
  getNewContext,
  draw,
  drawLinkedTextBoxStyle,
  setShadow,
  drawLine,
  drawPolygon,
} from "../../draw/index.js";
import drawHandles from "../../draw/drawHandles.js";
import math from "core/math";
import { lengthCursor } from "../cursors/index.js";

// Utils
import calculateSUV from "component/util/calculateSUV.js";
import numbersWithCommas from "component/util/numbersWithCommas.js";
import throttle from "component/util/throttle.js";
import { getEnabledElement } from "component/toolManager/enabledElements.js";
import getModalityLUT from "component/molecules/viewport/core/lookupTable/getModalityLUT.js";
import Helper from "helper";
/**
 * @public
 * @class LineTool
 * @memberof Tools.Annotation
 * @classdesc Tool for drawing line
 * @extends Tools.Base.BaseAnnotationTool
 */
export default class PolyLineTool extends BaseAnnotationTool {
  constructor(configuration = {}) {
    const defaultConfig = {
      name: "pencil",
      supportedInteractionTypes: ["Mouse", "Touch"],
      svgCursor: lengthCursor,
      configuration: { showMinMax: true },
      styles: {
        joiningDashLine: [2, 3],
      },
    };
    const initialConfiguration = Object.assign(defaultConfig, configuration);

    super(initialConfiguration);

    this.initialConfiguration = initialConfiguration;
  }

  createNewMeasurement(eventData) {
    const { image } = eventData;
    //if (!image || !image.pixelSpacing) {
    // 1. 영상 데이터가 없다면, 그리기를 생성하지 않음
    if (!image) {
      return;
    }

    // 2. US 영상일 경우, Physical Delta/Physical Direction Unit
    // 이외 영상일 경우, PixelSpacing이 없다면 그리기를 생성하지 않음
    const modality = image.modality;
    if (
      Helper.Validator.isDefined(modality) === false ||
      Helper.Validator.isString(modality) === false
    ) {
      return;
    }

    if (modality.toLowerCase() === "us") {
      const units = Helper.DICOM.getPhysicalDirectionUnit(image);
      if (units.x === -1 || units.y === -1) {
        return;
      }

      if (
        image.hasPhysicalDelta === false &&
        image.isExistPixelSpacing === false
      ) {
        return;
      }
    } else {
      if (
        image.isExistPixelSpacing === false &&
        image.usePixelSpacingApproximateValue === false
      ) {
        return;
      }
    }
    const goodEventData =
      eventData && eventData.currentPoints && eventData.currentPoints.image;

    if (!goodEventData) {
      return;
    }

    const { x, y } = eventData.currentPoints.image;

    return {
      visible: true,
      active: true,
      color: undefined,
      invalidated: true,
      storeMiddlePoint: true,
      middlePoint: [],
      cacheLinePoints: [],
      handles: {
        start: {
          x,
          y,
          highlight: true,
          active: false,
        },
        end: {
          x,
          y,
          highlight: true,
          active: true,
        },
        textBox: {
          active: false,
          hasMoved: false,
          movesIndependently: false,
          drawnIndependently: true,
          allowedOutsideImage: true,
          hasBoundingBox: true,
        },
      },
      willbeRemove: true,
      moveCount: 0,
    };
  }

  /**
   * @description coord 점이 그려진 도구 데이터와 특정 거리 이내에 있는지 없는지 판별
   *
   * @param {HTMLElement} element   canvas element
   * @param {Array<Point>} data  source point array
   * @param {Point} coords  target point
   * @returns {Boolean}
   */
  pointNearTool(element, data, coords) {
    const hasStartAndEndHandles =
      data && data.handles && data.handles.start && data.handles.end;
    const validParameters = hasStartAndEndHandles;

    if (!validParameters) {
      return false;
    }

    if (data.visible === false) {
      return false;
    }

    ///
    /// 전체 시작/끝 점과 중간점을 한 배열에 저장한다.
    ///

    /// 중간점을 새로운 배열에 복사한다.
    let linePoints = JSON.parse(JSON.stringify(data.middlePoint)); //참조없는 복사

    /// 시작점을 점 배열의 앞부분에 삽입한다.
    linePoints.unshift({
      x: data.handles.start.x,
      y: data.handles.start.y,
    });

    /// 끝점을 점 배열의 앞부분에 삽입한다.
    linePoints.push({
      x: data.handles.end.x,
      y: data.handles.end.y,
    });

    /// 좌표를 모두 canvas내 그려진 image 기준 좌표로 변환한다.
    linePoints.forEach((value, index) => {
      linePoints[index] = math.coordinate.pixelToCanvas(element, value);
    });

    /// 점 그룹과 특정점이 가까이 있는지 체크
    return math.point.getMinDistance(linePoints, coords) < 25;
  }

  /**
   * @description 도구 정보를 이용해 도구 렌더링
   * @method renderToolData
   *
   * @param {EVENT} evt
   * @returns
   * @memberof LengthTool
   */
  renderToolData(evt) {
    const eventData = evt.detail;
    const { handleRadius, drawHandlesOnHover } = this.configuration;
    const toolData = getToolState(evt.currentTarget, this.name);

    if (!toolData) {
      return;
    }
    let { image, element } = eventData;
    //if (!image || !image.pixelSpacing) {
    if (!image) {
      return;
    }

    const modality = image.modality;

    // us 추가부분
    if (!image.pixelSpacing && modality.toLowerCase() != "us") {
      return;
    }

    let hasUnit = true;
    if (modality.toLowerCase() === "us") {
      const ultrasoundRegions = image.ultrasoundRegions;
      if (
        Helper.Validator.isDefined(ultrasoundRegions) === true &&
        ultrasoundRegions.length > 0
      ) {
        if (
          !(ultrasoundRegions[0]["unitsX"] !== 3) ||
          !(ultrasoundRegions[0]["unitsY"] !== 3)
        ) {
          hasUnit = false;
        }
      } else {
        hasUnit = false;
      }
    }
    //
    const lineWidth = toolStyle.getToolWidth();
    const context = getNewContext(eventData.canvas);

    // Pixel Spacing
    const ps = Helper.DICOM.getPixelSpacingByModality(modality, image);
    const hasPixelSpacing = image.isExistPixelSpacing;
    // if (!image || !hasPixelSpacing) {
    //   return;
    // }

    image = image || {};
    const pixelSpacing = {
      rowPixelSpacing: ps.x || 1,
      columnPixelSpacing: ps.y || 1,
    };
    ///
    /// 저장된 전체 Freeform shape 를 모두 캔버스에 그림
    ///
    for (let i = 0; i < toolData.data.length; i++) {
      const data = toolData.data[i];

      if (data.visible === false || data.willbeRemove === true) {
        continue;
      }

      ///
      /// 한 개 Freeform shape 정보를 이용해 Freeform shape 그림
      ///
      draw(context, (context) => {
        /// 1. Context에 그려지는 도형에 대한 그림자 설정
        setShadow(context, this.configuration);
        const color = toolColors.getColorIfActive(data);

        /// 2. 그려진 점 Path를 배열에 저장
        let linePoints = JSON.parse(JSON.stringify(data.middlePoint)); //참조없는 복사
        linePoints.unshift({
          x: data.handles.start.x,
          y: data.handles.start.y,
        });

        linePoints.push({
          x: data.handles.end.x,
          y: data.handles.end.y,
        });

        /// 3. Canvas에 Path를 Polygon 형태로 그림
        const polygonOptions = {
          color: "#ff0000ff",
          lineWidth,
          strokeColor: toolColors.getFreeformShapeStrokeColorIfActive(data),
          fillStyle: toolColors.getFreeformShapeFillColorIfActive(data),
        };
        drawPolygon(context, element, linePoints, polygonOptions, "pixel");

        /// 4. 제어점을 잇는 선을 그림
        const guideColor = toolColors.getSecondaryColorIfActive(data);
        drawLine(
          context,
          element,
          data.handles.end,
          data.handles.start,
          {
            lineDash: [20, 15],
            color: guideColor,
          },
          "pixel"
        );
        // 5. 제어점을 그림
        const handleOptions = {
          color,
          handleRadius,
          drawHandlesIfActive: drawHandlesOnHover,
        };
        drawHandles(context, eventData, data.handles, handleOptions);

        /// 6. 사용자가 그린 Path 영역 내에 포함되는 영상좌표 추출
        const roiPoints = retrieveCoordsInROI(linePoints);
        data.volumeImageCoords = roiPoints; // 영상 좌표 캐쉬에 저장

        // 그려진 영역에 대한 통계 정보 계산 및 최신화

        if (data.invalidated === true) {
          if (data.cachedStats) {
            _throttledUpdateCachedStats(
              image,
              element,
              data,
              modality,
              pixelSpacing
            );
          } else {
            _updateCachedStats(image, element, data, modality, pixelSpacing);
          }
        }

        /// 7. 그려진 관심영역에 대한 통계 정보를 우측에  텍스트 박스를 이용하여 가시화
        if (!data.handles.textBox.hasMoved) {
          data.handles.textBox.x = Math.max(
            data.handles.start.x,
            data.handles.end.x
          );
          data.handles.textBox.y =
            (data.handles.start.y + data.handles.end.y) / 2;
        }
        const textBoxAnchorPoints = (handles) =>
          _findTextBoxAnchorPoints(handles.start, handles.end);
        const textBoxContent = _createTextBoxContent(
          context,
          image.color,
          data.cachedStats,
          modality,
          hasPixelSpacing,
          image.usePixelSpacingApproximateValue,
          image.hasPhysicalDelta,
          hasUnit,
          image,
          this.configuration
        );
        const linkColor = toolColors.getLinkColorIfActive(data);
        drawLinkedTextBoxStyle(
          context,
          element,
          data.handles.textBox,
          textBoxContent,
          data.handles,
          textBoxAnchorPoints,
          color,
          linkColor,
          lineWidth,
          0,
          true
        );
      });
    }
  }
}
/**
 * @description
 * @param  {} roiPoints
 */
function retrieveCoordsInROI(roiPoints) {
  /// 1. Path 영역이 그려진 대략전이 영역을 얻기 위해
  /// Canvas에 그려진 Path 영역의 좌상단, 우하단 좌표를 획득
  let ltPoint = { x: Number.MAX_VALUE, y: Number.MAX_VALUE };
  let rbPoint = { x: Number.MIN_VALUE, y: Number.MIN_VALUE };
  for (let i = 0; i < roiPoints.length; i++) {
    ltPoint.x = Math.min(ltPoint.x, roiPoints[i].x);
    ltPoint.y = Math.min(ltPoint.y, roiPoints[i].y);
    rbPoint.x = Math.max(rbPoint.x, roiPoints[i].x);
    rbPoint.y = Math.max(rbPoint.y, roiPoints[i].y);
  }

  /// 2. Dummy Canvas 생성
  /// 이때, 실제 Path 가 그려진 영역 만큼만 Canvas의 너비를 구성
  let dummyCanvas = document.createElement("canvas");
  dummyCanvas.width = rbPoint.x - ltPoint.x + 1;
  dummyCanvas.height = rbPoint.y - ltPoint.y + 1;
  let dummyCanvasContext = dummyCanvas.getContext("2d");

  /// 3. Path의 좌표를 최 좌상단 좌표를 기준으로한 상대좌표로 변환
  let dummyLinePoints = roiPoints.map((pt) => {
    return {
      x: pt.x - ltPoint.x,
      y: pt.y - ltPoint.y,
    };
  });

  /// 4. Dummy Canvas에 Path를 그리고, 영역을 흰색으로칠한다.
  fillPolygon(dummyCanvasContext, dummyLinePoints, "#ffffffff", "#ffffffff");

  /// 5. Dummy Canvas에 그려진 흰색 영역의 좌표 추출을 통해 Path 영역의 좌표를 획득
  let volumeImageCoords = [];
  var pixelData = dummyCanvasContext.getImageData(
    0,
    0,
    dummyCanvas.width,
    dummyCanvas.height
  );
  var buffer = new Uint32Array(pixelData.data.buffer);
  for (let y = 0; y < dummyCanvas.height; y++) {
    for (let x = 0; x < dummyCanvas.width; x++) {
      let index = y * dummyCanvas.width + x;
      if (buffer[index] === 0xffffffff) {
        volumeImageCoords.push({ x: x + ltPoint.x, y: y + ltPoint.y });
      }
    }
  }

  /// 6. 수집된 Path 영역 내부 좌표 배열 반환
  return volumeImageCoords;
}

//temp
function fillPolygon(context, pointsArray, fillColor, strokeColor) {
  if (pointsArray.length <= 0) return;
  context.beginPath();
  context.moveTo(pointsArray[0].x, pointsArray[0].y);
  for (var i = 0; i < pointsArray.length; i++) {
    context.lineTo(pointsArray[i].x, pointsArray[i].y);
  }
  if (strokeColor !== null && strokeColor !== undefined)
    context.strokeStyle = strokeColor;

  if (fillColor !== null && fillColor !== undefined) {
    context.fillStyle = fillColor;
    context.fill();
  }
  context.closePath();
  //context.restore();
}
//

const _throttledUpdateCachedStats = throttle(_updateCachedStats, 110);

function _updateCachedStats(image, element, data, modality, pixelSpacing) {
  const stats = _calculateStats(image, element, data, modality, pixelSpacing);

  data.cachedStats = stats;
  data.invalidated = false;
}

function _calculateStats(image, element, data, modality, pixelSpacing) {
  // 관심 영역에 포함되는 픽셀 값 배열 획득
  const pixels = getPixels(element, data.volumeImageCoords);
  // 픽셀 및 사각영역 정보를 이용해 관심영역내 평균과 표준편차를 계산
  const roiMeanStdDev = _calculatedPolygonStats(pixels);

  let alpha = 1;
  // 관심 영역의 너비/높이 정보와 Pixel spacing 정보를 이용하여 이미지 영역의 너비를 계산
  let area =
    data.volumeImageCoords.length *
    (pixelSpacing.columnPixelSpacing || 1) *
    (pixelSpacing.rowPixelSpacing || 1) *
    0.01;
  if (modality.toLowerCase() === "us") {
    const units = Helper.DICOM.getPhysicalDirectionUnit(image);
    if (units.x === "cm") {
      alpha = 100;
    } else {
      area = 0;
    }
  }
  if (modality.toLowerCase() === "pt") {
    roiMeanStdDev.mean = calculateSUV(image, roiMeanStdDev.mean, true) || 0;
    roiMeanStdDev.stdDev = calculateSUV(image, roiMeanStdDev.stdDev, true) || 0;
    roiMeanStdDev.min = calculateSUV(image, roiMeanStdDev.min, true) || 0;
    roiMeanStdDev.max = calculateSUV(image, roiMeanStdDev.max, true) || 0;
  }
  area = area * alpha;

  let stats = {
    area: area || 0,
    count: roiMeanStdDev.count || 0,
    mean: roiMeanStdDev.mean || 0,
    variance: roiMeanStdDev.variance || 0,
    stdDev: roiMeanStdDev.stdDev || 0,
    min: roiMeanStdDev.min || 0,
    max: roiMeanStdDev.max || 0,
  };

  if (modality.toLowerCase() === "pt") {
    if (!!image.isSupportSUV === true) {
      stats["meanSUV"] = calculateSUV(image, roiMeanStdDev.mean, true) || 0;
      stats["stdDevSUV"] = calculateSUV(image, roiMeanStdDev.stdDev, true) || 0;
      stats["minSUV"] = calculateSUV(image, roiMeanStdDev.min, true) || 0;
      stats["maxSUV"] = calculateSUV(image, roiMeanStdDev.max, true) || 0;
    }
  }

  return stats;
}

function getPixels(element, volumeImageCoords, filter = null) {
  const storedPixels = getStoredPixels(element, volumeImageCoords, filter);

  const ee = getEnabledElement(element);
  const mlutfn = getModalityLUT(
    ee.image.rescaleSlope,
    ee.image.rescaleIntercept,
    ee.viewport.modalityLUT
  );

  return storedPixels.map(mlutfn);
}

function getStoredPixels(element, volumeImageCoords, filter) {
  if (element === undefined) {
    throw new Error("getStoredPixels: parameter element must not be undefined");
  }

  const enabledElement = getEnabledElement(element);
  const storedPixels = [];
  let index = 0;

  if (enabledElement.image === undefined) {
    return null;
  }

  const imageWidth = enabledElement.image.columns;
  const imageHeight = enabledElement.image.rows;

  //const pixelData = enabledElement.image.getPixelData();
  const pixelData = enabledElement.image.pixelData;

  for (let i = 0; i < volumeImageCoords.length; i++) {
    const xPos = Math.round(volumeImageCoords[i].x);
    const yPos = Math.round(volumeImageCoords[i].y);
    if (xPos < 0 || xPos >= imageWidth || yPos < 0 || yPos >= imageHeight) {
      continue;
    }
    // 필터는 추후 고려
    const spIndex = yPos * enabledElement.image.columns + xPos;
    storedPixels[index++] = pixelData[spIndex];
  }

  return storedPixels;
}

function _calculatedPolygonStats(pixels) {
  let sum = 0;
  let sumSquared = 0;
  let count = 0;
  let index = 0;
  let min = pixels ? pixels[0] : null;
  let max = pixels ? pixels[0] : null;

  for (let i = 0; i < pixels.length; i++) {
    sum += pixels[index];
    sumSquared += pixels[index] * pixels[index];
    min = Math.min(min, pixels[index]);
    max = Math.max(max, pixels[index]);
    count++;
    index++;
  }

  if (count === 0) {
    return {
      count,
      mean: 0.0,
      variance: 0.0,
      stdDev: 0.0,
      min: 0.0,
      max: 0.0,
    };
  }

  const mean = sum / count;
  const variance = sumSquared / count - mean * mean;

  return {
    count,
    mean,
    variance,
    stdDev: Math.sqrt(variance),
    min,
    max,
  };
}
/**
 * @description 텍스트 상자의 Anchor가 걸릴 수 있는 점을 지정
 * @param  {{x:number, y:number}} startHandle
 * @param  {{x:number, y:number}} endHandle
 * @returns {Array.<{x:number, y:number}>}
 */
function _findTextBoxAnchorPoints(startHandle, endHandle) {
  return [
    {
      x: (startHandle.x + endHandle.x) / 2,
      y: (startHandle.y + endHandle.y) / 2,
    },
  ];
}

/**
 *
 *
 * @param {*} area
 * @param {*} hasPixelSpacing
 * @returns {string} 출력되는 넓이 정보를 형식화된 단위로 변환
 */
function _formatArea(area, hasPixelSpacing, hasUnit) {
  //  Char code 178 : 넓이 표현을 위해 사용한 윗첨자 2 문자
  let suffix = "";
  if (hasUnit) {
    suffix = hasPixelSpacing
      ? //? ` mm${String.fromCharCode(178)}`
        ` cm${String.fromCharCode(178)}`
      : ` px${String.fromCharCode(178)}`;
  }

  return `  Area=${numbersWithCommas(area.toFixed(2))}${suffix}`; // for mm
  //return `Area: ${numbersWithCommas((area / 10).toFixed(2))}${suffix}`; // for cm
}

/**
 *
 *
 * @param {*} hasPixelSpacing
 * @returns {string} 출력되는 넓이 정보를 형식화된 단위로 변환
 */
function _formatAreaUnit(hasPixelSpacing) {
  let suffix = hasPixelSpacing
    ? //? ` mm${String.fromCharCode(178)}`
      ` cm${String.fromCharCode(178)}`
    : ` px${String.fromCharCode(178)}`;

  return suffix;
}

/**
 * TODO: This is identical to EllipticalROI's same fn
 * TODO: We may want to make this a utility for ROIs with these values?
 *
 * @param {*} context
 * @param {*} isColorImage
 * @param {*} { area, mean, stdDev, min, max, meanStdDevSUV }
 * @param {*} modality
 * @param {*} hasPixelSpacing
 * @param {*} [options={}]
 * @returns {string[]}
 */
function _createTextBoxContent(
  context,
  isColorImage,
  { area, mean, stdDev, min, max, count, meanSUV, stdDevSUV, minSUV, maxSUV },
  modality,
  hasPixelSpacing,
  hasAproximatePixelSpacing,
  hasPhysicalDelta,
  hasUnit,
  image,
  options = {}
) {
  const showMinMax = options.showMinMax || false;
  const showHounsfieldUnits = options.showHounsfieldUnits !== false;
  const textLines = [];

  const otherLines = [];

  const normalTextStyle = {
    font: textStyle.getFont(),
    fontSize: textStyle.getFontSize(),
    fontFamily: textStyle.getFontFamily(),
    textBaseline: "top",
    fontColor: textStyle.getFontColor(),
  };

  const defaultFontSize = textStyle.getFontSize();
  const unitTextStyle = {
    font: `${defaultFontSize}px arial Regular`,
    fontSize: textStyle.getFontSize(),
    textBaseline: "top",
    fontColor: textStyle.getFontColor(),
  };

  const showStatisticalInfo = count > 0 ? true : false;

  if (!isColorImage && showStatisticalInfo) {
    let meanString = `Mean=${numbersWithCommas(mean.toFixed(2))}`;
    const stdDevString = `SD=${numbersWithCommas(stdDev.toFixed(2))}`;

    otherLines.push(`  ${meanString} ${stdDevString}`);

    if (showMinMax) {
      const minString = `Min=${min.toFixed(2)} `;
      let maxString = `Max=${max.toFixed(2)} `;

      otherLines.push(`  ${maxString} ${minString}`);
    }
  }

  if (modality.toUpperCase() === "US") {
    if (hasUnit === true || hasPixelSpacing || hasPhysicalDelta) {
      const units = Helper.DICOM.getPhysicalDirectionUnit(image);
      if (units.x === "cm") {
        /// display area
        textLines.push([
          _formatArea(
            area,
            hasPixelSpacing | hasPhysicalDelta,
            false /* hasUnit*/
          ),
          normalTextStyle,
          false,
        ]);

        /// display area unit
        textLines.push([
          _formatAreaUnit(hasPixelSpacing | hasPhysicalDelta),
          unitTextStyle,
          false,
        ]);
        textLines.push([" (" + count + " px)", normalTextStyle, true]);
      } else {
        textLines.push(["  Area=" + count + " px", normalTextStyle, true]);
      }
    } else {
      textLines.push(["  Area=" + count + " px", normalTextStyle, true]);
    }
  } else {
    if (hasPixelSpacing === true || hasAproximatePixelSpacing === true) {
      /// display area
      textLines.push([
        _formatArea(
          area,
          hasPixelSpacing | hasAproximatePixelSpacing,
          false /* hasUnit*/
        ),
        normalTextStyle,
        false,
      ]);

      /// display area unit
      textLines.push([
        _formatAreaUnit(hasPixelSpacing | hasAproximatePixelSpacing),
        unitTextStyle,
        false,
      ]);

      /// display pixel count
      textLines.push([" (" + count + " px)", normalTextStyle, true]);
    } else {
      /// display pixel count
      textLines.push(["  Area=" + count + " px", normalTextStyle, true]);
    }
  }
  //otherLines.forEach(x => textLines.push([x, normalTextStyle, true]));
  otherLines
    .slice()
    .reverse()
    .forEach((x) => textLines.unshift([x, normalTextStyle, true]));

  if (modality.toLowerCase() === "pt") {
    if (!!image.isSupportSUV === true) {
      textLines.push(["", normalTextStyle, true]);
      textLines.push(["  SUVbw (g/ml)", normalTextStyle, true]);
      let meanString = `Mean=${numbersWithCommas(meanSUV.toFixed(2))}`;
      const stdDevString = `SD=${numbersWithCommas(stdDevSUV.toFixed(2))}`;
      textLines.push([
        `  ${meanString} ${stdDevString}`,
        normalTextStyle,
        true,
      ]);
      const minString = `Min=${minSUV.toFixed(2)} `;
      let maxString = `Max=${maxSUV.toFixed(2)} `;
      textLines.push([`  ${maxString} ${minString}`, normalTextStyle, true]);
    }
  }

  return textLines;
}
